import { useQuery } from '@tanstack/react-query';
import { PanelCard } from '../ui/PanelCard';
import { getAudit } from '../../lib/api';
import type { AuditEntry } from '../../lib/types';

function timeAgoPt(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr}h`;
  const d = Math.floor(hr / 24);
  return `há ${d}d`;
}

function initials(name: string): string {
  if (!name) return '—';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);
}

function ZapIcon() {
  return (
    <svg
      width={10}
      height={10}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  );
}

export function RecentActivity() {
  const query = useQuery({
    queryKey: ['audit', { limit: 6 }],
    queryFn: () => getAudit({ limit: 6 }),
    refetchInterval: 60_000,
  });

  return (
    <PanelCard title="Atividade recente">
      {query.isLoading ? (
        <div className="p-4 text-xs text-textMute">Carregando…</div>
      ) : query.error ? (
        <div className="p-4 text-xs text-danger">
          Erro ao carregar atividade
        </div>
      ) : !query.data || query.data.length === 0 ? (
        <div className="p-6 text-center text-xs text-textMute">
          Sem atividade recente
        </div>
      ) : (
        query.data.slice(0, 6).map((a: AuditEntry, i: number) => {
          const auto = !a.userId || a.userName === 'system';
          return (
            <div
              key={a.id}
              className={
                'px-3.5 py-2 flex items-center gap-2.5 text-[11px]' +
                (i > 0 ? ' border-t border-borderSoft' : '')
              }
            >
              <div
                className={
                  'w-[22px] h-[22px] rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ' +
                  (auto
                    ? 'bg-accentSoft text-accent'
                    : 'bg-surfaceAlt text-textMute')
                }
              >
                {auto ? <ZapIcon /> : initials(a.userName)}
              </div>
              <div className="flex-1 min-w-0">
                <b className="text-text">{a.userName || '—'}</b>{' '}
                <span className="text-textMute">{a.action.toLowerCase()}</span>{' '}
                <code className="font-mono text-[10px] bg-surfaceAlt text-text px-1.5 py-px rounded-[3px]">
                  {a.target
                    ? a.target.length > 22
                      ? a.target.slice(0, 22) + '…'
                      : a.target
                    : '—'}
                </code>
              </div>
              <span className="font-mono text-[10px] text-textSoft shrink-0">
                {timeAgoPt(a.at)}
              </span>
            </div>
          );
        })
      )}
    </PanelCard>
  );
}

export default RecentActivity;
